import React from 'react';

import { RequiredFlag } from '../../required-flag';
import { Option } from '../option';
import { Select } from '../select';
import { SelectOption, SingleOption, isSingleOption } from '../select-types';
import useSelectInputV2 from './select-input-v2.controller';

const ID = 'select-input-v2';
export interface SelectInputV2Props<T> {
    id?: string;
    ariaLabel?: string;
    label?: React.ReactNode;
    hideLabel?: boolean;
    helpMessage?: React.ReactNode;

    options: SelectOption<T>[];
    onSelect?: (option: SingleOption<T> | null) => void;
    value?: string | null;
    searchValueOverwrite?: string;

    onSearch?: (value: string) => void;
    onScrollToBottom?: () => void;

    required?: boolean;
    disabled?: boolean;
    valid?: boolean;
    errorMessage?: React.ReactNode;
    isValidCallback?: (valid: boolean) => void;

    isClearable?: boolean;
    isLoading?: boolean;

    className?: string;
    selectClassName?: string;
    selectContainerClassName?: string;

    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    hidePlaceholder?: boolean;
    disableSearch?: boolean;

    useUrlOverwrite?: string;
    /**
     * @description Will check the current URL for a query param that matches this value.
     * If found (using the fetch function) the value will be set.
     * When selecting a new value, the URL will be updated.
     * Do not use with useUrlOverwrite (may cause unexpected behaviour)
     */
    useQueryParam?: string;
}

export const SelectInputV2 = <T,>(props: SelectInputV2Props<T>) => {
    const viewProps = useSelectInputV2<T>(props);

    return (
        <div
            id={ID}
            className={'flex w-full flex-col items-start justify-start gap-1 ' + (props.className ? props.className : '')}>
            {props.label && !props.hideLabel && (
                <div className='mb-0! flex w-full flex-col'>
                    <div className='inline-flex items-center gap-1'>
                        <span className='text-foreground-default text-sm font-medium'>{props.label}</span>
                        <RequiredFlag required={props.required} />
                    </div>

                    {props.helpMessage && <small className='form-text text-muted'>{props.helpMessage}</small>}
                </div>
            )}
            <Select
                disableSearch={props.disableSearch}
                containerClassName={props.selectContainerClassName}
                className={props.selectClassName}
                id={props.id || ID}
                ariaLabel={props.ariaLabel}
                onSelect={viewProps.onSelect}
                selected={viewProps.selected}
                hidePlaceholder={props.hidePlaceholder}
                value={viewProps.value}
                disabled={props.disabled}
                optionsLoading={props.isLoading}
                onChange={(v) => {
                    viewProps.onSearch(v);
                    viewProps.isValid();
                }}
                isClearable={props.isClearable}
                onScrollToBottom={props.onScrollToBottom}
                onFocus={(e) => {
                    props.onFocus && props.onFocus(e);
                    viewProps.setTouched(true);
                }}
                onBlur={props.onBlur}>
                {viewProps.searchResultOptions &&
                    viewProps.searchResultOptions.map((option) => {
                        if (isSingleOption(option)) {
                            return (
                                <Option
                                    key={option.value}
                                    {...option}
                                />
                            );
                        }
                        return (
                            <React.Fragment key={option.label}>
                                <li
                                    className='list-group-item option text-foreground-muted pointer-events-none border-none px-3 py-1.5 text-xs font-semibold tracking-wide uppercase'
                                    role='presentation'>
                                    {option.display ?? option.label}
                                </li>
                                {option.options.map((childOption: SingleOption<T>) => (
                                    <Option
                                        key={childOption.value}
                                        {...childOption}
                                    />
                                ))}
                            </React.Fragment>
                        );
                    })}
            </Select>
            {viewProps.touched && !viewProps.valid && (
                <span className='text-status-danger mt-1 font-medium'>
                    <small>{props.errorMessage ? props.errorMessage : 'Please select a valid option.'}</small>
                </span>
            )}
        </div>
    );
};

export default SelectInputV2;
